import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';

const BASE_URL = 'http://localhost:8000';

const getServices  = ()         => api.get('/admin/services').then(r => r.data);
const createService = (data)    => api.post('/admin/services', data).then(r => r.data);
const updateService = (id, data)=> api.put(`/admin/services/${id}`, data).then(r => r.data);
const toggleService = (id)      => api.put(`/admin/services/${id}/toggle`).then(r => r.data);
const uploadImage   = (id, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post(`/admin/services/${id}/upload-image`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};

const CATEGORIES = ['AC', 'Plumbing', 'Electrical', 'Cleaning', 'Appliance Repair', 'Water Purifier', 'Carpentry', 'Painting', 'Pest Control', 'Beauty'];
const emptyForm  = { name: '', category: '', description: '', base_price: '', duration_minutes: 60, image_url: '' };

export default function Services() {
  const [services, setServices]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [editId, setEditId]       = useState(null);
  const [editRowId, setEditRowId] = useState(null); // for inline edit scroll
  const [form, setForm]           = useState(emptyForm);
  const [saving, setSaving]       = useState(false);
  const [search, setSearch]       = useState('');
  // image upload state
  const [imageFile, setImageFile]     = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading]     = useState(false);
  // upload-only modal (for existing services)
  const [uploadTarget, setUploadTarget] = useState(null);
  const [uploadFile, setUploadFile]     = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const fileRef   = useRef();
  const uploadRef = useRef();
  const editRef   = useRef();

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [svcs, cats] = await Promise.all([
        getServices(),
        api.get('/categories/').then(r => r.data).catch(() => []),
      ]);
      setServices(svcs);
      setCategories(cats.map(c => c.name));
    } catch (e) {}
    setLoading(false);
  };

  const openAdd = () => {
    setForm({ ...emptyForm, category: categories[0] || 'AC' });
    setEditId(null); setEditRowId(null);
    setImageFile(null); setImagePreview(null);
    setShowForm(true);
    setTimeout(() => editRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
  };

  const openEdit = (s) => {
    setForm({ name: s.name, category: s.category, description: s.description || '',
              base_price: s.base_price, duration_minutes: s.duration_minutes, image_url: s.image_url || '' });
    setEditId(s.id);
    setEditRowId(s.id); // mark which row to show form under
    setImageFile(null);
    setImagePreview(s.image_url ? resolveUrl(s.image_url) : null);
    setShowForm(true);
    setTimeout(() => editRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
  };

  const resolveUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return BASE_URL + url;
  };

  const onFileChange = (e, setter, previewSetter) => {
    const file = e.target.files[0];
    if (!file) return;
    setter(file);
    previewSetter(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!form.name || !form.base_price) { alert('Name and price are required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, base_price: parseFloat(form.base_price), duration_minutes: parseInt(form.duration_minutes) };
      let saved;
      if (editId) saved = await updateService(editId, payload);
      else        saved = await createService(payload);

      // upload image if a file was selected
      if (imageFile) {
        setUploading(true);
        await uploadImage(saved.id ?? editId, imageFile);
        setUploading(false);
      }
      setShowForm(false);
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const handleToggle = async (id) => {
    try { await toggleService(id); load(); } catch (e) { alert('Failed'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Permanently delete "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/services/${id}`);
      alert('Service deleted!');
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Delete failed'); }
  };

  // Quick-upload modal for existing services
  const openUploadModal = (s) => {
    setUploadTarget(s);
    setUploadFile(null);
    setUploadPreview(s.image_url ? resolveUrl(s.image_url) : null);
  };

  const handleQuickUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      await uploadImage(uploadTarget.id, uploadFile);
      setUploadTarget(null);
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Upload failed'); }
    setUploading(false);
  };

  const isAnimated = (url) => url && (url.endsWith('.gif') || url.endsWith('.webp') || url.endsWith('.apng'));

  return (
    <div>
      <div className="page-header">
        <h1>Services Management</h1>
        <p>Add, edit and manage platform services with images</p>
      </div>

      {/* ── Add / Edit Form ── */}
      {showForm && !editRowId && (
        <div className="card" style={{ marginBottom: 24 }} ref={editRef}>
          <div className="card-header">
            <h2>{editId ? '✏️ Edit Service' : '➕ Add New Service'}</h2>
            <button className="btn btn-sm btn-danger" onClick={() => setShowForm(false)}>✗ Cancel</button>
          </div>
          <div style={{ padding: '0 20px 20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={lbl}>Service Name *</label>
              <input style={inp} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. AC Repair" />
            </div>
            <div>
              <label style={lbl}>Category *</label>
              <select style={inp} value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                {(categories.length > 0 ? categories : CATEGORIES).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Base Price (₹) *</label>
              <input style={inp} type="number" value={form.base_price} onChange={e => setForm({ ...form, base_price: e.target.value })} placeholder="499" />
            </div>
            <div>
              <label style={lbl}>Duration (minutes)</label>
              <input style={inp} type="number" value={form.duration_minutes} onChange={e => setForm({ ...form, duration_minutes: e.target.value })} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={lbl}>Description</label>
              <textarea style={{ ...inp, height: 70, resize: 'vertical' }} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Service description..." />
            </div>

            {/* Image Upload */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={lbl}>Service Image / Animated GIF</label>
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                {/* Preview box */}
                <div
                  onClick={() => fileRef.current.click()}
                  style={{
                    width: 140, height: 140, border: '2px dashed #c4b5fd', borderRadius: 12,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', background: '#faf5ff', overflow: 'hidden', flexShrink: 0,
                    position: 'relative',
                  }}
                >
                  {imagePreview ? (
                    <>
                      <img src={imagePreview} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      {isAnimated(imageFile?.name || form.image_url) && (
                        <div style={{ position: 'absolute', bottom: 4, right: 4, background: '#7c3aed', color: '#fff', fontSize: 9, padding: '2px 5px', borderRadius: 4 }}>ANIMATED</div>
                      )}
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: 32 }}>🖼️</span>
                      <span style={{ fontSize: 11, color: '#7c3aed', marginTop: 6, textAlign: 'center', padding: '0 8px' }}>Click to upload</span>
                    </>
                  )}
                </div>

                <div style={{ flex: 1, minWidth: 200 }}>
                  <input
                    ref={fileRef} type="file"
                    accept="image/*,.gif,.webp,.apng,.avif,.svg,.jfif"
                    style={{ display: 'none' }}
                    onChange={e => onFileChange(e, setImageFile, setImagePreview)}
                  />
                  <button type="button" className="btn btn-secondary" style={{ marginBottom: 8 }} onClick={() => fileRef.current.click()}>
                    📁 Choose File
                  </button>
                  {imageFile && <div style={{ fontSize: 12, color: '#555', marginBottom: 6 }}>📎 {imageFile.name} ({(imageFile.size/1024).toFixed(1)} KB)</div>}
                  <div style={{ fontSize: 12, color: '#888', lineHeight: 1.6 }}>
                    Supported: <strong>JPG, JPEG, JFIF, PNG, GIF, WebP, SVG, APNG, AVIF, BMP, ICO, TIFF</strong><br />
                    ✅ Animated GIF & WebP supported<br />
                    Max recommended: 5MB
                  </div>
                  {imagePreview && (
                    <button type="button" style={{ marginTop: 8, fontSize: 12, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}
                      onClick={() => { setImageFile(null); setImagePreview(null); setForm({ ...form, image_url: '' }); }}>
                      🗑 Remove image
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <button className="btn btn-success" onClick={handleSave} disabled={saving || uploading}>
                {uploading ? '⬆️ Uploading image...' : saving ? 'Saving...' : (editId ? '💾 Update Service' : '✅ Create Service')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Quick Upload Modal ── */}
      {uploadTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setUploadTarget(null)}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>🖼️ Upload Image — {uploadTarget.name}</h2>
              <button onClick={() => setUploadTarget(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>✕</button>
            </div>

            {/* Preview */}
            <div
              onClick={() => uploadRef.current.click()}
              style={{
                width: '100%', height: 180, border: '2px dashed #c4b5fd', borderRadius: 12,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', background: '#faf5ff', overflow: 'hidden', marginBottom: 16,
              }}
            >
              {uploadPreview ? (
                <img src={uploadPreview} alt="preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              ) : (
                <div style={{ textAlign: 'center', color: '#7c3aed' }}>
                  <div style={{ fontSize: 40 }}>🖼️</div>
                  <div style={{ fontSize: 13, marginTop: 8 }}>Click to choose image / GIF</div>
                </div>
              )}
            </div>

            <input
              ref={uploadRef} type="file"
              accept="image/*,.gif,.webp,.apng,.avif,.svg,.jfif"
              style={{ display: 'none' }}
              onChange={e => onFileChange(e, setUploadFile, setUploadPreview)}
            />

            {uploadFile && <div style={{ fontSize: 12, color: '#555', marginBottom: 12 }}>📎 {uploadFile.name} ({(uploadFile.size / 1024).toFixed(1)} KB)</div>}

            <div style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>
              Supported: JPG, PNG, GIF (animated), WebP (animated), SVG, APNG, AVIF, BMP, ICO, TIFF
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" style={{ flex: 1 }} disabled={!uploadFile || uploading} onClick={handleQuickUpload}>
                {uploading ? '⬆️ Uploading...' : '⬆️ Upload Image'}
              </button>
              <button className="btn btn-secondary" onClick={() => setUploadTarget(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Services Table ── */}
      <div className="card">
        <div className="card-header">
          <h2>🛠️ All Services ({services.length})</h2>
          <button className="btn btn-primary" onClick={openAdd}>➕ Add Service</button>
        </div>
        {/* Search */}
        <div style={{ padding: '0 20px 12px' }}>
          <input
            style={{ ...inp, maxWidth: 400 }}
            placeholder="🔍 Search by ID, name, or category..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        {loading ? <div className="loading">Loading services...</div> : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Image</th>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Price</th>
                  <th>Duration</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {services
                  .filter(s => {
                    if (!search.trim()) return true;
                    const q = search.toLowerCase();
                    return String(s.id).includes(q) ||
                           s.name.toLowerCase().includes(q) ||
                           s.category.toLowerCase().includes(q);
                  })
                  .map(s => (
                  <React.Fragment key={s.id}>
                    <tr>
                      <td>
                        <div onClick={() => openUploadModal(s)} title="Click to upload / change image"
                          style={{ width: 56, height: 56, borderRadius: 10, overflow: 'hidden', background: '#f3f4f6',
                            border: '2px dashed #d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', position: 'relative' }}>
                          {s.image_url ? (
                            <>
                              <img src={resolveUrl(s.image_url)} alt={s.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              {isAnimated(s.image_url) && (
                                <div style={{ position: 'absolute', bottom: 1, right: 1, background: '#7c3aed', color: '#fff', fontSize: 8, padding: '1px 3px', borderRadius: 3 }}>GIF</div>
                              )}
                            </>
                          ) : (
                            <span style={{ fontSize: 22 }}>📷</span>
                          )}
                        </div>
                      </td>
                      <td>#{s.id}</td>
                      <td><strong>{s.name}</strong></td>
                      <td><span className="badge badge-info">{s.category}</span></td>
                      <td><strong>₹{s.base_price}</strong></td>
                      <td>{s.duration_minutes} min</td>
                      <td>
                        <span className={`badge ${s.is_active ? 'badge-success' : 'badge-danger'}`}>
                          {s.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button className="btn btn-sm btn-warning" onClick={() => openEdit(s)}>✏️ Edit</button>
                          <button className="btn btn-sm btn-primary" onClick={() => openUploadModal(s)}>🖼️ Image</button>
                          <button className={`btn btn-sm ${s.is_active ? 'btn-danger' : 'btn-success'}`} onClick={() => handleToggle(s.id)}>
                            {s.is_active ? '🚫 Disable' : '✅ Enable'}
                          </button>
                          <button className="btn btn-sm" style={{background:'#666',color:'#fff'}} onClick={() => handleDelete(s.id, s.name)}>🗑️ Delete</button>
                        </div>
                      </td>
                    </tr>
                    {/* Inline edit form — appears right below the row being edited */}
                    {showForm && editRowId === s.id && (
                      <tr>
                        <td colSpan="8" style={{ padding: 0 }}>
                          <div ref={editRef} style={{ background: '#f8f5ff', padding: 20, borderTop: '2px solid #6C63FF' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                              <h3 style={{ margin: 0, color: '#6C63FF' }}>✏️ Editing: {s.name}</h3>
                              <button onClick={() => { setShowForm(false); setEditRowId(null); }}
                                style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#999' }}>✕</button>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                              <div>
                                <label style={lbl}>Name *</label>
                                <input style={inp} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                              </div>
                              <div>
                                <label style={lbl}>Category *</label>
                                <select style={inp} value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                                  {(categories.length > 0 ? categories : CATEGORIES).map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                              </div>
                              <div>
                                <label style={lbl}>Price (₹) *</label>
                                <input style={inp} type="number" value={form.base_price} onChange={e => setForm({ ...form, base_price: e.target.value })} />
                              </div>
                              <div>
                                <label style={lbl}>Duration (min)</label>
                                <input style={inp} type="number" value={form.duration_minutes} onChange={e => setForm({ ...form, duration_minutes: e.target.value })} />
                              </div>
                              <div style={{ gridColumn: '1 / -1' }}>
                                <label style={lbl}>Description</label>
                                <textarea style={{ ...inp, height: 60 }} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                              </div>
                              <div>
                                <button className="btn btn-success" onClick={handleSave} disabled={saving || uploading}>
                                  {saving ? 'Saving...' : '💾 Update Service'}
                                </button>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const lbl = { display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 600, color: '#555' };
const inp = { width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };
